<script setup>
import { ref, computed, watch, inject, onMounted, onUnmounted, reactive } from "vue";
import NProgress from "nprogress";
import { useRouter, useRoute } from "vue-router";
import { TrashIcon, PlayIcon, ArrowPathIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import { ClockIcon, CopyIcon, ImageIcon } from "lucide-vue-next";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import ResourceTable from "@Components/Table/ResourceTable.vue";
import ResourceTableHeaders from "@Components/Table/ResourceTableHeaders.vue";
import ResourceTableHeader from "@Components/Table/ResourceTableHeader.vue";
import ResourceTableBody from "@Components/Table/ResourceTableBody.vue";
import SearchInput from "@Components/Table/Search/SearchInput.vue";
import ActionsMenu from "@Components/Table/Actions/ActionsMenu.vue";
import ActionsMenuItem from "@Components/Table/Actions/ActionsMenuItem.vue";
import FormListbox from "@Components/Form/Listbox.vue";
import Pagination from "@Components/Pagination.vue";
import { statuses } from "@Utils/publication";
import PublicationTableRow from "./Partials/PublicationTableRow.vue";
import ProjectDetails from "./Partials/ProjectDetails.vue";
import { useForm } from "@/form";
import { useI18n } from "vue-i18n";
import Breadcrumb from "@Components/Breadcrumb.vue";
import _cloneDeep from "lodash/cloneDeep";
import {
    FormCheckbox,
    FormCheckboxGroup,
    FormCheckboxGroupOption,
    FormInput,
    FormLabel,
    FormModelListbox,
    FormRadioGroup,
    FormRadioGroupOption,
    FormSwitch,
} from "@Components/Form";
import { DestroyModal, ConfirmModal, FormModal } from "@Components/Modal";
import { planningDays, planningPeriods } from "@Utils/project";
import Timepicker from "@Components/Form/Timepicker.vue";
import { PrimaryButton } from "@Components/Button";
import Datepicker from "@Components/Form/Datepicker.vue";
import _map from "lodash/map";
import _groupBy from "lodash/groupBy";
import { format } from "date-fns";
import { models as imagesModels, getProvider as getImageProvider, getGroup as getImageGroup } from "@Utils/image-models";

const $notify = inject("$notify");
const router = useRouter();
const route = useRoute();
const { t: __ } = useI18n();

const publications = ref();
const project = ref();

const form = useForm({
    project: route.params.id,
    page: route.query.page ? parseInt(route.query.page) : 1,
    sort_order: route.query?.sort_order,
    sort_direction: route.query?.sort_direction,
    search: route.query?.search,
    status: route.query?.status,
});

const bulkPublishForm = useForm({
    publications: [],
    filters: {},
});

const bulkDestroyForm = useForm({
    publications: [],
    filters: {},
});

const bulkRegenerateForm = useForm({
    publications: [],
    filters: {},
});

const bulkCopyUrlsForm = useForm({
    publications: [],
    filters: {},
});

const bulkReplanifyingForm = useForm({
    publications: [],
    filters: {},
    planning: {
        enabled: true,
        start_date: format(new Date(), "yyyy-MM-dd"),
        days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
        per_period: 1,
        period: "day",
        start_time: {
            hours: 6,
            minutes: 30,
        },
        end_time: {
            hours: 21,
            minutes: 0,
        },
    },
});

const bulkRegenerateThumbnailForm = useForm({
    publications: [],
    filters: {},
    model: "gpt_image_1_medium",
    override: false,
});

const processing = ref(false);

const bulkDestroyModal = ref();
const bulkRegenerateModal = ref();
const bulkReplanifyingModal = ref();
const bulkRegenerateThumbnailModal = ref();

const selectedItems = reactive({
    current: false,
    all: false,
    selected: [],
});

// methods
const fetch = async (clear) => {
    NProgress.start();
    processing.value = true;
    const response = await form.post("publications_index");
    project.value = response.project;
    publications.value = response.publications;
    if (clear) {
        selectedItems.current = false;
        selectedItems.all = false;
        selectedItems.selected = [];
    }
    processing.value = false;
    NProgress.done();
};

const bulkPublish = () => {
    if (bulkPublishForm.processing) return;
    if (selectedItemsLength.value > 0) {
        bulkPublishForm.publications = _cloneDeep(selectedItems);
        bulkPublishForm.filters = _cloneDeep(form.data());
        bulkPublishForm
            .post("publications_bulk_publish")
            .then(() => {
                $notify({
                    title: __("Les publications sélectionnées seront bientôt publiées."),
                    type: "success",
                });

                bulkPublishForm.reset();

                fetch(true);
            })
            .catch(() => {
                $notify({
                    title: "An error occurred while publishing.",
                    type: "error",
                });

                bulkPublishForm.reset();
            });
    }
};

const bulkCopyUrls = () => {
    if (bulkCopyUrlsForm.processing) return;
    if (selectedItemsLength.value > 0) {
        bulkCopyUrlsForm.publications = _cloneDeep(selectedItems);
        bulkCopyUrlsForm.filters = _cloneDeep(form.data());
        bulkCopyUrlsForm
            .post("publications_bulk_copy_urls")
            .then(async (data) => {
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(data.join("\n"));

                    $notify({
                        title: __("Les URLs des publications sélectionnées ont été copiées dans le presse-papiers."),
                        type: "success",
                    });
                }

                bulkCopyUrlsForm.reset();
            })
            .catch((e) => {
                console.log(e);
                $notify({
                    title: "An error occurred while copying the URLs.",
                    type: "error",
                });

                bulkCopyUrlsForm.reset();
            });
    }
};

const showBulkDestroyModal = () => {
    if (selectedItemsLength.value > 0) {
        bulkDestroyForm.publications = _cloneDeep(selectedItems);
        bulkDestroyForm.filters = _cloneDeep(form.data());
        bulkDestroyModal.value.show();
    }
};

const bulkDestroy = async () => {
    if (bulkDestroyForm.processing) return;
    if (selectedItemsLength.value > 0) {
        bulkDestroyForm
            .post("publications_bulk_destroy")
            .then(() => {
                bulkDestroyModal.value.hide();

                $notify({
                    title: __("Les publications sélectionnées ont été supprimées."),
                    type: "success",
                });

                bulkDestroyForm.reset();

                fetch(true);
            })
            .catch(() => {
                bulkDestroyModal.value.hide();

                $notify({
                    title: "An error occurred while deleting.",
                    type: "error",
                });

                bulkDestroyForm.reset();
            });
    }
};

const showBulkRegenerateModal = () => {
    if (selectedItemsLength.value > 0) {
        bulkRegenerateForm.publications = _cloneDeep(selectedItems);
        bulkRegenerateForm.filters = _cloneDeep(form.data());
        bulkRegenerateModal.value.show();
    }
};

const bulkRegenerate = async () => {
    if (bulkRegenerateForm.processing) return;
    if (selectedItemsLength.value > 0) {
        bulkRegenerateForm
            .post("publications_bulk_regenerate")
            .then(() => {
                bulkRegenerateModal.value.hide();

                $notify({
                    title: __("Les publications sélectionnées seront publiées prochainement."),
                    type: "success",
                });

                bulkRegenerateForm.reset();

                fetch(true);
            })
            .catch(() => {
                bulkRegenerateModal.value.hide();

                $notify({
                    title: "An error occurred while publishing.",
                    type: "error",
                });

                bulkRegenerateForm.reset();
            });
    }
};

const showBulkReplanifyingModal = () => {
    if (selectedItemsLength.value > 0) {
        bulkReplanifyingForm.publications = _cloneDeep(selectedItems);
        bulkReplanifyingForm.filters = _cloneDeep(form.data());
        bulkReplanifyingModal.value.show();
    }
};

const bulkReplanifying = async () => {
    if (bulkReplanifyingForm.processing) return;
    if (selectedItemsLength.value > 0) {
        bulkReplanifyingForm
            .post("publications_bulk_replanifying")
            .then(() => {
                bulkReplanifyingModal.value.hide();

                $notify({
                    title: __("Les publications sélectionnées ont été replanifiées."),
                    type: "success",
                });

                bulkReplanifyingForm.reset();

                fetch(true);
            })
            .catch(() => {
                bulkReplanifyingModal.value.hide();

                $notify({
                    title: "An error occurred while replanifying.",
                    type: "error",
                });

                bulkReplanifyingForm.reset();
            });
    }
};

const showBulkRegenerateThumbnailModal = () => {
    if (selectedItemsLength.value > 0) {
        bulkRegenerateThumbnailForm.publications = _cloneDeep(selectedItems);
        bulkRegenerateThumbnailForm.filters = _cloneDeep(form.data());
        bulkRegenerateThumbnailModal.value.show();
    }
};

const bulkRegenerateThumbnail = async () => {
    if (bulkRegenerateThumbnailForm.processing) return;
    if (selectedItemsLength.value > 0) {
        bulkRegenerateThumbnailForm
            .post("publications_bulk_regenerate_thumbnail")
            .then(() => {
                bulkRegenerateThumbnailModal.value.hide();

                $notify({
                    title: __("Les images de mise en avant des publications sélectionnées sont en cours de regénération."),
                    type: "success",
                });

                bulkRegenerateThumbnailForm.reset();

                fetch(true);
            })
            .catch(() => {
                bulkRegenerateThumbnailModal.value.hide();

                $notify({
                    title: "An error occurred while regenerating the thumbnails.",
                    type: "error",
                });

                bulkRegenerateThumbnailForm.reset();
            });
    }
};

const updateSelections = (key) => {
    if (key === "all") {
        selectedItems.current = !selectedItems.all ? true : selectedItems.current;
        selectedItems.all = !selectedItems.all;
    } else if (key === "current") {
        selectedItems.current = !selectedItems.current;
        selectedItems.all = selectedItems.current ? false : selectedItems.all;
    }

    if (selectedItems.current || selectedItems.all) {
        selectedItems.selected = [];
        for (let i = 0; i < publications.value.data.length; i++) {
            selectedItems.selected.push(publications.value.data[i].id);
        }
    } else if (!selectedItems.current) {
        selectedItems.selected = [];
    }
};

// computed
const sort = computed({
    get() {
        return {
            order: form.sort_order,
            direction: form.sort_direction,
        };
    },
    set(newValue) {
        form.sort_order = newValue.order;
        form.sort_direction = newValue.direction;
    },
});

const selectedItemsLength = computed(() => {
    if (selectedItems.all) {
        return publications.value.total;
    } else if (selectedItems.current) {
        return publications.value.data.length;
    }

    return selectedItems.selected.length;
});

const imagesModelsGroups = _map(_groupBy(_cloneDeep(imagesModels), "group"), (items, group) => {
    return {
        name: getImageGroup(group).name,
        items: items,
    };
});

// watchers
watch(
    () => form.data(),
    async () => {
        router.push({ name: "publications.index", query: form.data() });
        await fetch(true);
    },
    { deep: true }
);

watch(
    () => route.query,
    (params) => {
        if (params.p) {
            form.page = parseInt(params.p);
        }
    }
);
// ---

await fetch(true);

const interval = ref();
onMounted(() => {
    interval.value = setInterval(() => {
        fetch(false);
    }, 15 * 1000);
});

onUnmounted(() => {
    clearInterval(interval.value);
});
</script>

<template>
    <div>
        <Breadcrumb :pages="[{ name: __('Mes projets'), href: { name: 'projects.index' } }, { name: __('Projet #{id}', { id: project.id }) }]" />
        <ProjectDetails :project="project" @updated="fetch(true)" />

        <div class="pt-11 border-t border-gray-300 dark:border-gray-800">
            <h2 class="px-4 text-base font-semibold leading-7 text-gray-900 dark:text-white lg:px-8 sm:px-6">{{ __("Publications") }}</h2>

            <div class="w-full flex flex-col items-center gap-x-3 gap-y-6 px-4 mt-6 lg:px-8 sm:flex-row sm:px-6">
                <div class="w-full lg:max-w-[24rem]">
                    <SearchInput :label="__('Titre de la publication')" v-model.lazy="form.search" />
                </div>
                <div class="w-full lg:max-w-[12rem]">
                    <FormListbox :label="__('Statut')" v-model="form.status" :items="statuses" item-key="id" item-display="name"></FormListbox>
                </div>
                <ActionsMenu class="w-full lg:ml-auto lg:max-w-[20rem]" :label="__('Actions groupées')" :disabled="selectedItemsLength === 0">
                    <ActionsMenuItem @click="bulkPublish">
                        <PlayIcon class="flex-shrink-0 w-5 h-5 mr-4" aria-hidden="true" />
                        <span class="block truncate">{{ __("Publier maintenant") }}</span>
                    </ActionsMenuItem>
                    <ActionsMenuItem @click="showBulkReplanifyingModal">
                        <ClockIcon class="flex-shrink-0 w-5 h-5 mr-4" aria-hidden="true" />
                        <span class="block truncate">{{ __("Replanifier") }}</span>
                    </ActionsMenuItem>
                    <ActionsMenuItem @click="showBulkRegenerateModal">
                        <ArrowPathIcon class="flex-shrink-0 w-5 h-5 mr-4" aria-hidden="true" />
                        <span class="block truncate">{{ __("Regénérer / Réessayer") }}</span>
                    </ActionsMenuItem>
                    <ActionsMenuItem @click="showBulkRegenerateThumbnailModal">
                        <ImageIcon class="flex-shrink-0 w-5 h-5 mr-4" aria-hidden="true" />
                        <span class="block truncate">{{ __("Regénérer l'image de mise en avant") }}</span>
                    </ActionsMenuItem>
                    <ActionsMenuItem @click="bulkCopyUrls">
                        <CopyIcon class="flex-shrink-0 w-5 h-5 mr-4" aria-hidden="true" />
                        <span class="block truncate">{{ __("Copier les URLs") }}</span>
                    </ActionsMenuItem>
                    <ActionsMenuItem @click="showBulkDestroyModal">
                        <TrashIcon class="flex-shrink-0 w-5 h-5 mr-4" aria-hidden="true" />
                        <span class="block truncate">{{ __("Supprimer") }}</span>
                    </ActionsMenuItem>
                </ActionsMenu>
            </div>

            <ResourceTable class="mt-6">
                <ResourceTableHeaders>
                    <ResourceTableHeader class="pl-4 lg:pl-8 sm:pl-6">
                        <div class="-ml-2">
                            <Popover class="relative" v-slot="{ open }">
                                <PopoverButton
                                    :class="[
                                        open ? 'ring-2' : '',
                                        'px-2 bg-white dark:bg-gray-950 py-1 group focus:outline-none rounded-md inline-flex items-center text-gray-600 dark:text-gray-400 ring-offset-2 ring-gray-900 dark:ring-gray-800 dark:ring-offset-gray-950 focus:ring-2',
                                    ]">
                                    <FormCheckbox
                                        disabled
                                        :modelValue="selectedItems.current || selectedItems.all"
                                        class="pointer-events-none disabled:opacity-100" />
                                    <ChevronDownIcon class="w-5 h-5 ml-2" aria-hidden="true" />
                                </PopoverButton>

                                <transition
                                    enter-active-class="transition duration-200 ease-out"
                                    enter-from-class="opacity-0 translate-y-1"
                                    enter-to-class="opacity-100 translate-y-0"
                                    leave-active-class="transition duration-150 ease-in"
                                    leave-from-class="opacity-100 translate-y-0"
                                    leave-to-class="opacity-0 translate-y-1">
                                    <PopoverPanel class="transform w-screen max-w-sm absolute z-10 px-2 mt-3 sm:px-0">
                                        <div
                                            class="ring-opacity-5 overflow-hidden relative px-4 py-3 space-y-3 bg-white rounded-lg ring-1 ring-black shadow-lg dark:bg-gray-900 dark:ring-gray-800">
                                            <label class="flex items-center">
                                                <FormCheckbox :modelValue="selectedItems.current" @update:modelValue="updateSelections('current')" />
                                                <div class="ml-3 text-sm font-normal">
                                                    {{ __("Sélectionner la page courante") }} ({{ publications.data.length }})
                                                </div>
                                            </label>
                                            <label class="flex items-center">
                                                <FormCheckbox :modelValue="selectedItems.all" @update:modelValue="updateSelections('all')" />
                                                <div class="ml-3 text-sm font-normal">{{ __("Tout sélectionner") }} ({{ publications.total }})</div>
                                            </label>
                                        </div>
                                    </PopoverPanel>
                                </transition>
                            </Popover>
                        </div>
                    </ResourceTableHeader>
                    <ResourceTableHeader column="title" v-model="sort" :sortable="true" class="w-full">{{ __("Titre de la publication") }}</ResourceTableHeader>
                    <ResourceTableHeader column="status">{{ __("Statut") }}</ResourceTableHeader>
                    <ResourceTableHeader column="published_at" v-model="sort" :sortable="true" class="hidden lg:table-cell">{{
                        __("Date de publication")
                    }}</ResourceTableHeader>
                    <ResourceTableHeader class="pr-4 lg:pr-8 sm:pr-6">
                        <span class="sr-only">Edit</span>
                    </ResourceTableHeader>
                </ResourceTableHeaders>
                <ResourceTableBody>
                    <PublicationTableRow
                        v-model="selectedItems"
                        v-for="publication in publications.data"
                        :key="publication.id"
                        :publication="publication"
                        :publications="publications"
                        :project="project"
                        @updated="fetch(true)" />
                </ResourceTableBody>
            </ResourceTable>

            <Pagination
                v-if="publications.last_page > 1"
                class="px-4 py-4 border-t border-gray-300 dark:border-gray-800 lg:px-8 md:px-6 sm:px-6"
                v-model="form.page"
                :items="publications"
                :disabled="processing" />
        </div>

        <DestroyModal
            ref="bulkDestroyModal"
            @destroy="bulkDestroy"
            :title="__('Supprimer les publications sélectionnées')"
            :description="__('Êtes-vous sûr de vouloir supprimer les publications sélectionnées ? Cette action ne peut pas être annulée.')" />

        <ConfirmModal
            ref="bulkRegenerateModal"
            @confirm="bulkRegenerate"
            :title="__('Regénérer les publications sélectionnées')"
            :description="__('Êtes-vous sûr de vouloir regénérer les publications sélectionnées ? Cette action ne peut pas être annulée.')"
            :label="__('Regénérer')" />

        <FormModal ref="bulkReplanifyingModal" @submit="bulkReplanifying" :title="__('Replanifier les publications sélectionnées')" class="sm:max-w-5xl">
            <template #body>
                <div class="space-y-12">
                    <FormSwitch
                        :modelValue="!bulkReplanifyingForm.planning.enabled"
                        @update:modelValue="($event) => (bulkReplanifyingForm.planning.enabled = !$event)"
                        :label="__('Publier toutes les publications maintenant')"
                        :z="1" />

                    <template v-if="bulkReplanifyingForm.planning.enabled">
                        <div class="flex flex-wrap items-center gap-3">
                            <div class="flex items-center gap-3">
                                <FormLabel>{{ __("Publier") }}</FormLabel>
                                <div class="max-w-[7rem]">
                                    <FormInput type="number" min="1" v-model="bulkReplanifyingForm.planning.per_period" :z="1" />
                                </div>
                            </div>
                            <div class="flex items-center gap-3">
                                <FormLabel>{{ __("publications par") }}</FormLabel>
                                <FormListbox v-model="bulkReplanifyingForm.planning.period" :items="planningPeriods" required :z="1"></FormListbox>
                            </div>
                            <div class="flex items-center gap-3">
                                <FormLabel>{{ __("à partir du") }}</FormLabel>
                                <div class="max-w-sm">
                                    <Datepicker v-model="bulkReplanifyingForm.planning.start_date" :z="1" />
                                </div>
                            </div>
                        </div>

                        <FormCheckboxGroup v-model="bulkReplanifyingForm.planning.days" :label="__('Jours de publication')">
                            <div class="grid grid-cols-1 gap-y-6 mt-4 sm:grid-cols-8 sm:gap-x-4">
                                <FormCheckboxGroupOption
                                    v-for="option in planningDays"
                                    :key="option.id"
                                    :modelValue="bulkReplanifyingForm.planning.days.includes(option.id)"
                                    @update:modelValue="
                                        $event
                                            ? bulkReplanifyingForm.planning.days.push(option.id)
                                            : bulkReplanifyingForm.planning.days.splice(bulkReplanifyingForm.planning.days.indexOf(option.id), 1)
                                    "
                                    :z="1">
                                    <template #title>
                                        <div class="flex justify-center">{{ __(option.name) }}</div>
                                    </template>
                                </FormCheckboxGroupOption>
                            </div>
                        </FormCheckboxGroup>

                        <div>
                            <FormLabel>{{ __("Heures de publication (UTC)") }}</FormLabel>
                            <div class="max-w-xs grid grid-cols-2 gap-4 mt-4">
                                <Timepicker v-model="bulkReplanifyingForm.planning.start_time" :z="1" />
                                <Timepicker v-model="bulkReplanifyingForm.planning.end_time" :z="1" />
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <template #actions>
                <div class="flex justify-end items-center">
                    <PrimaryButton type="submit" :z="1">
                        {{ __("Replanifier") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>

        <FormModal
            ref="bulkRegenerateThumbnailModal"
            @submit="bulkRegenerateThumbnail"
            :title="__('Regénérer l\'image de mise en avant des publications sélectionnées')"
            class="sm:max-w-3xl">
            <template #body>
                <div class="space-y-6">
                    <FormModelListbox v-model="bulkRegenerateThumbnailForm.model" :groups="imagesModelsGroups" required :label="__('Modèle d\'images')">
                        <template v-slot:details="{ item }">
                            <div class="flex flex-col gap-3 p-3">
                                <div>
                                    <span class="text-xs text-gray-600 dark:text-gray-400">{{ getImageProvider(item.provider).name }}&nbsp;/&nbsp;</span>
                                    <span class="text-xs text-gray-900 dark:text-white">{{ item.name }}</span>
                                </div>
                                <div v-if="item.description" class="w-[14rem] text-xs text-gray-600 dark:text-gray-400">{{ item.description }}</div>
                                <div v-if="item.quality">
                                    <span class="text-xs text-gray-900 dark:text-white">{{ __("Qualité") }}</span>
                                    <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                        <div
                                            v-for="i in 10"
                                            :key="i"
                                            :class="i <= item.quality ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                            class="h-1 rounded-full"></div>
                                    </div>
                                </div>
                                <div v-if="item.speed">
                                    <span class="text-xs text-gray-900 dark:text-white">{{ __("Vitesse") }}</span>
                                    <div class="w-[14rem] grid grid-cols-10 gap-1 mt-1">
                                        <div
                                            v-for="i in 10"
                                            :key="i"
                                            :class="i <= item.speed ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-800'"
                                            class="h-1 rounded-full"></div>
                                    </div>
                                </div>
                                <div v-if="item.price">
                                    <span class="text-xs text-gray-900 dark:text-white">${{ item.price }}</span>
                                    <span class="text-xs text-gray-600 dark:text-gray-400">&nbsp;/image</span>
                                </div>
                            </div>
                        </template>
                    </FormModelListbox>
                    <FormSwitch v-model="bulkRegenerateThumbnailForm.override" :label="__('Remplacer l\'image de mise en avant si elle existe')" :z="1" />
                </div>
            </template>
            <template #actions>
                <div class="flex justify-end items-center">
                    <PrimaryButton type="submit" :z="1">
                        {{ __("Regénérer") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>
    </div>
</template>
